<template>
  <div class="analysis-card">
    <!-- 顶部：总数与维度筛选 -->
    <div class="analysis-card-header">
      <div class="analysis-total">客户 {{ total }}</div>
      <a-select size="small" :value="dimension" class="filter-select">
        <a-select-option value="all">全部维度</a-select-option>
        <a-select-option value="area">区域维度</a-select-option>
        <a-select-option value="channel">渠道维度</a-select-option>
      </a-select>
    </div>

    <!-- 标签切换：重点客户 / 客户画像 -->
    <div class="analysis-header">
      <div class="analysis-tabs">
        <div
          v-for="tab in tabs"
          :key="tab.key"
          class="analysis-tab"
          :class="{ active: active === tab.key }"
          @click="active = tab.key"
        >
          {{ tab.label }}
        </div>
      </div>
    </div>

    <!-- 重点客户 -->
    <div v-if="active === 'key'" class="analysis-key">
      <div class="analysis-key-left">
        <div class="key-level-list">
          <div class="key-level-item" v-for="item in keyLevels" :key="item.label">
            <div class="key-level-title">{{ item.label }}</div>
            <div class="key-level-sub">{{ item.sub }}</div>
          </div>
        </div>
      </div>
      <div class="analysis-key-chart">
        <div ref="keyPie" class="analysis-pie"></div>
      </div>
      <div class="analysis-key-right">
        <div class="key-consultant" v-for="item in consultants" :key="item.name">
          <span class="key-consultant-name">{{ item.name }}</span>
          <span class="key-consultant-value">{{ item.value }}</span>
        </div>
      </div>
    </div>

    <!-- 客户画像：性别维度 -->
    <div v-else class="analysis-profile">
      <div class="profile-left">
        <div class="profile-title">性别</div>
        <div ref="profilePie" class="analysis-pie"></div>
      </div>
      <div class="profile-right">
        <div class="profile-legend">
          <div class="profile-legend-row" v-for="item in genderList" :key="item.label">
            <div class="legend-label">
              <span class="legend-dot" :style="{ backgroundColor: item.color }"></span>
              <span>{{ item.label }}</span>
            </div>
            <div class="legend-value">{{ item.count }}</div>
            <div class="legend-percent">{{ item.percent }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'CustomerAnalysis',

  props: {
    total: { type: String, default: '' },
    dimension: { type: String, default: 'all' },
    keyLevels: { type: Array, default: () => [] },
    keyPieData: { type: Array, default: () => [] },
    consultants: { type: Array, default: () => [] },
    genderList: { type: Array, default: () => [] }
  },

  data() {
    return {
      active: 'key',
      tabs: [
        { key: 'key', label: '重点客户' },
        { key: 'profile', label: '客户画像' }
      ],
      keyChart: null,
      profileChart: null,
      resizeHandler: null
    }
  },

  watch: {
    keyPieData: {
      handler() {
        if (this.active === 'key') {
          this.renderKeyPie()
        }
      },
      deep: true
    },
    genderList: {
      handler() {
        if (this.active === 'profile') {
          this.renderProfilePie()
        }
      },
      deep: true
    },
    active() {
      this.$nextTick(() => {
        if (this.active === 'key') {
          this.renderKeyPie()
        } else {
          this.renderProfilePie()
        }
      })
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.renderKeyPie()
      this.resizeHandler = () => {
        if (this.keyChart) this.keyChart.resize()
        if (this.profileChart) this.profileChart.resize()
      }
      window.addEventListener('resize', this.resizeHandler)
    })
  },

  beforeDestroy() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
    if (this.keyChart) {
      this.keyChart.dispose()
      this.keyChart = null
    }
    if (this.profileChart) {
      this.profileChart.dispose()
      this.profileChart = null
    }
  },

  methods: {
    // 重点客户分布饼图
    renderKeyPie() {
      if (!this.$refs.keyPie) return
      if (this.keyChart) this.keyChart.dispose()
      this.keyChart = echarts.init(this.$refs.keyPie)
      this.keyChart.setOption({
        tooltip: { trigger: 'item' },
        legend: { show: false },
        series: [
          {
            type: 'pie',
            radius: ['45%', '70%'],
            center: ['50%', '50%'],
            label: {
              show: true,
              formatter: '{b} {d}%',
              fontSize: 12
            },
            labelLine: { show: true, length: 12, length2: 10 },
            data: this.keyPieData.map(item => ({
              name: item.label,
              value: item.value,
              itemStyle: { color: item.color }
            }))
          }
        ]
      })
    },
    // 客户画像：性别饼图
    renderProfilePie() {
      if (!this.$refs.profilePie) return
      if (this.profileChart) this.profileChart.dispose()
      this.profileChart = echarts.init(this.$refs.profilePie)
      this.profileChart.setOption({
        tooltip: { trigger: 'item' },
        legend: { show: false },
        series: [
          {
            type: 'pie',
            radius: ['45%', '70%'],
            center: ['50%', '50%'],
            label: { show: false },
            data: this.genderList.map(item => ({
              name: item.label,
              value: parseInt(item.count),
              itemStyle: { color: item.color }
            }))
          }
        ]
      })
    }
  }
}
</script>

<style lang="less" scoped>
.analysis-card {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 12px;
}
.analysis-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.analysis-total {
  font-size: 12px;
  color: #5b6b7c;
}
.analysis-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.analysis-tabs {
  display: flex;
  gap: 16px;
}
.analysis-tab {
  padding: 4px 0;
  cursor: pointer;
  color: #5b6b7c;
}
.analysis-tab.active {
  color: #1f2f3d;
  font-weight: 600;
  position: relative;
}
.analysis-tab.active::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -6px;
  height: 3px;
  border-radius: 2px;
  background: #2b6de5;
}
.analysis-key {
  display: grid;
  grid-template-columns: 180px 1fr 160px;
  gap: 12px;
  align-items: start;
}
.key-level-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.key-level-item {
  background: #f7f9fc;
  border: 1px solid #eef2f7;
  border-radius: 8px;
  padding: 10px;
}
.key-level-title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2f3d;
}
.key-level-sub {
  color: #7a8699;
  font-size: 12px;
}
.analysis-pie {
  height: 220px;
}
.key-consultant {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px dashed #eef2f7;
}
.key-consultant:last-child {
  border-bottom: none;
}
.key-consultant-name {
  color: #1f2f3d;
}
.key-consultant-value {
  color: #2b6de5;
}
.analysis-profile {
  display: grid;
  grid-template-columns: 1fr 220px;
  gap: 12px;
  align-items: start;
}
.profile-title {
  font-weight: 600;
  color: #1f2f3d;
  margin-bottom: 6px;
}
.profile-legend {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.profile-legend-row {
  display: grid;
  grid-template-columns: 1fr 70px 50px;
  gap: 8px;
  align-items: center;
  font-size: 12px;
}
.legend-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #5b6b7c;
}
.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}
</style>
